import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AlertCircleIcon } from 'lucide-react';
import { useDatabase } from '../../hooks/useDatabase';
import { usePublicLanding } from '../../hooks/usePublicContent';
import { submitLead } from '../../services/leadService';
import { readOrigin } from '../../utils/utm';
import { isUuid, logFailure } from '../../utils/diagnostics';
import { isValidUrl, postSubmitConfig, storeSubmission } from '../../utils/postSubmit';
import { resolveMetaPixel, trackMetaLead } from '../../utils/metaPixel';
import { useMetaPixelPageView } from '../../hooks/useMetaPixel';
import { LandingRenderer } from './LandingRenderer';
import { DynamicFormResult } from './DynamicForm';
import { Button } from '../ui/Button';
import { Form, LandingPage } from '../../types';
interface LandingViewProps {
  slug: string;
  /** Muestra los accesos a la plataforma en la barra superior (home de AVA). */
  showAppNav?: boolean;
  /** Contenido estático usado si la landing no existe en la base de datos. */
  fallback?: {
    landing: LandingPage;
    form?: Form;
  };
}
export function LandingView({
  slug,
  showAppNav = false,
  fallback
}: LandingViewProps) {
  const db = useDatabase();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const {
    status,
    message,
    retry
  } = usePublicLanding(slug);
  const landing = db.landings.find(l => l.slug === slug) ?? fallback?.landing;
  const organization = landing ? db.organizations.find(o => o.id === landing.organizationId) : undefined;
  const form = (landing?.formId ? db.forms.find(f => f.id === landing.formId) : undefined) ?? fallback?.form;

  // A. Landing visitada: PageView una sola vez por Pixel realmente resuelto
  // (sin Pixel configurado, cero peticiones a Meta). Se llama siempre, antes
  // de cualquier retorno anticipado, para no romper el orden de los hooks.
  const metaPixel = resolveMetaPixel(landing?.pageConfig.metaPixel);
  useMetaPixelPageView(metaPixel);

  // Mientras la consulta esté en curso NUNCA se muestra «no existe»: solo un
  // cargador limpio. El 404 se reserva para `no_encontrada`.
  if (status === 'cargando' && !landing) {
    return <main className="flex min-h-screen w-full items-center justify-center bg-background" aria-busy="true" data-id="element-866" data-mp="2d35xwp0h7bht">
        <span className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-primary" data-id="element-867" data-mp="39t6gg6a94vqo" />
        <span className="sr-only" data-id="element-868" data-mp="35s4idss2mpqa">Cargando la página…</span>
      </main>;
  }
  if (status === 'error' && !landing) {
    return <main className="flex min-h-screen w-full flex-col items-center justify-center gap-4 bg-background px-6 text-center" data-id="element-869" data-mp="30rp5117shi8b">
        <AlertCircleIcon className="h-10 w-10 text-muted-foreground" aria-hidden="true" data-id="element-870" data-mp="2vt5f6pq2rmao" />
        <h1 className="text-2xl font-semibold text-navy" data-id="element-871" data-mp="1peeaeaxtzhbd">
          No hemos podido cargar la página
        </h1>
        <p className="max-w-md text-sm text-muted-foreground" data-id="element-872" data-mp="1kd71u9nv6e3n">
          Comprueba tu conexión e inténtalo de nuevo.
          {message ? ` (${message})` : ''}
        </p>
        <Button onClick={retry} data-id="element-873" data-mp="1fapqwulrw1yw">Reintentar</Button>
      </main>;
  }
  if (!landing) {
    return <main className="flex min-h-screen w-full flex-col items-center justify-center gap-4 bg-background px-6 text-center" data-id="element-874" data-mp="1k8hgvj2uxe0g">
        <AlertCircleIcon className="h-10 w-10 text-muted-foreground" data-id="element-875" data-mp="1p10x6nsxin8y" />
        <h1 className="text-2xl font-semibold text-navy" data-id="element-876" data-mp="1t1uiraiwfpyo">Esta página no existe</h1>
        <p className="max-w-md text-sm text-muted-foreground" data-id="element-877" data-mp="1xzw6i8r9sdgh">
          La landing que buscas no está publicada o el enlace ha cambiado.
        </p>
        <Link to="/" data-id="element-878" data-mp="2300lfucj504l">
          <Button variant="secondary" data-id="element-879" data-mp="22xeg81d42wpj">Volver al inicio</Button>
        </Link>
      </main>;
  }

  // El logotipo de la landing hereda el de la empresa si no se ha definido uno.
  const resolved = {
    ...landing,
    pageConfig: {
      ...landing.pageConfig,
      logoUrl: landing.pageConfig.logoUrl || organization?.brand.logoUrl || organization?.logoUrl
    }
  };
  async function handleSubmit(result: DynamicFormResult) {
    if (!landing || !form) return;
    setError(null);

    // La plantilla estática de respaldo tiene ids que no son UUID: si se está
    // mostrando esa, no existe en la base de datos y no puede crear leads.
    if (!isUuid(form.id) || !isUuid(landing.id)) {
      logFailure('submitLead', {
        message: 'Se está mostrando la landing de ejemplo incluida en el código, no una landing real de la base de datos.',
        details: `landing_id="${landing.id}", form_id="${form.id}", slug="${slug}"`,
        hint: 'Publica tu landing desde el panel y visita su URL pública (/landing/tu-slug).'
      }, {
        landingId: landing.id,
        formId: form.id,
        slug
      });
      const message = 'Esta es la landing de ejemplo del sistema y no está conectada a ninguna empresa, por lo que no puede guardar contactos. Publica tu landing desde el panel y usa su enlace público.';
      setError(message);
      throw new Error(message);
    }
    try {
      await submitLead({
        organizationId: landing.organizationId,
        formId: form.id,
        landingId: landing.id,
        contact: result.contact,
        answers: result.answers,
        origin: readOrigin(location.search, {
          landingId: landing.id,
          formId: form.id
        })
      });
      // Se guarda el envío de ESTE visitante (por pestaña) para que la página
      // de gracias pueda personalizar el mensaje de WhatsApp con sus datos.
      storeSubmission(landing.slug, {
        contact: result.contact,
        answers: result.answers
      });
      // B. Lead completado: se dispara SOLO aquí, después de que submitLead()
      // haya confirmado que el envío se guardó correctamente. Si submitLead
      // lanza un error, este punto nunca se alcanza y no hay evento Lead.
      trackMetaLead(metaPixel);
      // El lead ya está guardado. Solo ahora se ejecuta la acción posterior
      // configurada en el formulario.
      const action = postSubmitConfig(form);
      if (action.action === 'url' && isValidUrl(action.redirectUrl)) {
        window.location.href = action.redirectUrl!.trim();
        return;
      }
      if (action.action === 'whatsapp') {
        // El botón y el mensaje se muestran dentro del propio formulario.
        return;
      }
      if (action.action === 'mensaje') return;

      // Flujo previsto: formulario multipaso → cualificación → calendario.
      // Si la landing tiene bloque de calendario, el visitante continúa ahí
      // sin salir de la página; si no, va a la página de gracias.
      const hasCalendar = landing.pageConfig.blocks.some(block => block.type === 'calendario' && block.visible);
      if (hasCalendar) {
        document.getElementById('calendario')?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
        return;
      }
      navigate(`/gracias/${landing.slug}`);
    } catch (err) {
      // El mensaje real se propaga al formulario, que lo muestra en línea.
      setError(err instanceof Error ? err.message : 'No hemos podido enviar tu solicitud.');
      throw err;
    }
  }
  return <main className="w-full bg-background" data-id="element-880" data-mp="36fdpqqyqvkv7">
      {error && <p role="alert" className="bg-red-50 px-5 py-3 text-center text-sm text-red-700" data-id="element-881" data-mp="36gp2hga642yg">
          {error}
        </p>}
      <LandingRenderer landing={resolved} form={form} onSubmit={handleSubmit} authLinks={showAppNav} brandName={organization?.name} data-id="element-882" data-mp="3bgtasj0x9rdl" />
    </main>;
}